import { groupGraphNodes } from "@/common";
import LoadingSpinner from "@imminently/decisively-ui/LoadingSpinner";
import { JsonEditor } from "@/common/JsonEditor";
import { useCurrentRelease } from "@/common/hooks";
import { scrollableMixin } from "@/common/scrollbar";
import styled from "@emotion/styled";
import { DecisionGraph, generatePayload } from "@imminently/decisively-core";
import Button from "@imminently/decisively-ui/Button";
import cls from "clsx";
import Autocomplete, { type AutocompleteProps } from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import TextField from "@mui/material/TextField";
import React from "react";
import Form, { Controller, FieldWatch, useFormState } from "@imminently/decisively-ui/Form";
import UITextField from "@imminently/decisively-ui/TextField";
import { z } from "zod";

export type IGoal = { label: string; value: string; group: string };

const StyledAutocomplete = styled(Autocomplete)`
  & .MuiAutocomplete-groupLabel {
    color: var(--foreground);
    font-size: 0.875rem;
  }
` as React.FC<AutocompleteProps<IGoal, false, false, false>>;

const CommonJsonWrap = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  overflow: auto;
  margin-bottom: 1rem;

  [name="body"] span:nth-of-type(2){
    ${scrollableMixin};
  }

  >*:first-child {
    flex-grow: 1;
    height: initial !important;
    overflow: auto;
  }
`;

const marignBottom05Rem = { marginBottom: "0.5rem" };

export const classes = {
  ">name": "name",
  ">goal": "goal",

  ">jsonLabel": "jsonLabel",
  ">json": "json",

  ">btns": "btns",
};

const GenerateButton = styled(Button)`
  max-height: initial;
  min-width: initial;

  >* { white-space: initial; }
`;

const groups = {
  goal: "goal",
  intermediate: "intermediate",
};

export type ITestScenario = {
  id: string;
  name: string;
  goal: string;
  outcome: string;
  payload: string;
  model: string;
  release: string;
  created: string;
  lastModified: string;
};

const isValidJson = (v: string | undefined) => {
  if (!v) return false;

  try {
    return Boolean(JSON.parse(v));
  } catch (e) {
    return false;
  }
};

const requiredString = z.string().min(1, "This field is required");
const stringJson = z.string().refine((value) => isValidJson(value), { message: "Is not valid Json" });

const testScenarioSchema = z.object({
  name: requiredString,
  goal: requiredString,
  payload: stringJson.refine(
    (value) => {
      const forbiddenFields = ["data", "goal", "mode"];
      const json = JSON.parse(value);
      return forbiddenFields.every((field) => !Object.prototype.hasOwnProperty.call(json, field));
    },
    { message: "Root fields ['data', 'goal', 'mode'] are not allowed" },
  ),
  outcome: stringJson,
});

export type IProps = {
  scenario: Pick<ITestScenario, "name" | "goal"> & { outcome: string; payload: string };
  onSubmit: (d: Pick<ITestScenario, "name" | "goal" | "outcome" | "payload">) => unknown;
  onGenerate?: (data: Pick<IProps["scenario"], "payload" | "goal">, setOutput: (v: string) => unknown) => unknown;
  className?: string;
};

type ScenarioFormValues = Pick<ITestScenario, "name" | "goal" | "outcome" | "payload">;

type ScenarioFormBodyProps = {
  form: any;
  className?: string;
  goals: IGoal[];
  generating: boolean;
  isSubmitting: boolean;
  payloadUsedForGenerate: string;
  setPayloadUsedForGenerate: React.Dispatch<React.SetStateAction<string>>;
  setGenerating: React.Dispatch<React.SetStateAction<boolean>>;
  onGenerate?: (data: Pick<IProps["scenario"], "payload" | "goal">, setOutput: (v: string) => unknown) => unknown;
};

const ScenarioFormBody: React.FC<ScenarioFormBodyProps> = ({
  form,
  className,
  goals,
  generating,
  isSubmitting,
  payloadUsedForGenerate,
  setPayloadUsedForGenerate,
  setGenerating,
  onGenerate,
}) => {
  const maybeCurrentRelease = useCurrentRelease();
  const formState = useFormState();
  const isValid = formState?.isValid ?? false;
  const errors = (formState?.errors as any) || {};

  type OnGoalChange = (_: unknown, e: string | IGoal | (string | IGoal)[] | null) => unknown;
  const onGoalChange = React.useCallback<OnGoalChange>(
    (_, e) => {
      if (Array.isArray(e) || typeof e === "string") return;

      form.setValue("goal", e === null ? "" : e.value, { shouldValidate: true });
      if (e === null) return;
      if (!maybeCurrentRelease) return;

      const payload = generatePayload(e.value, true, maybeCurrentRelease, maybeCurrentRelease.rule_graph);
      if (typeof payload !== "object" || payload === null || !("data" in payload) || typeof payload.data !== "object")
        return;

      form.setValue("payload", JSON.stringify(payload.data, null, 2));
    },
    [form, maybeCurrentRelease],
  );

  const onGenerateHandler = React.useCallback(() => {
    if (onGenerate === undefined) return undefined;
    form.trigger(["name", "goal", "payload"]).then((valid) => {
      if (valid) {
        setGenerating(true);
        const { goal, payload } = form.getValues();
        onGenerate({ goal, payload }, (v) => {
          form.setValue("outcome", JSON.stringify(v, null, 2));
          if (JSON.stringify(v) !== "{}") setPayloadUsedForGenerate(payload);
          setGenerating(false);
        });
      }
    });
  }, [onGenerate, form, setGenerating, setPayloadUsedForGenerate]);

  const circularProgressStyle = React.useMemo<any>(
    () => ({ width: 30, height: 30, visibility: generating ? "visible" : "hidden" }),
    [generating],
  );

  return (
    <FieldWatch names={["payload"]}>
      {({ payload: watchedPayload }) => {
        const generateDisabled = Boolean(
          generating || watchedPayload === payloadUsedForGenerate || errors.name || errors.goal || errors.payload,
        );
        const saveIsDisabled = !isValid;

        return (
          <div className={cls("flex h-full w-full flex-col", className)}>
            <div className="flex flex-col gap-6">
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-12 md:col-span-6">
                  <UITextField
                    name="name"
                    label="Name *"
                    className={classes[">name"]}
                    fullWidth
                  />
                </div>

                <div className="col-span-12 md:col-span-6">
                  <Controller
                    control={form.control}
                    name="goal"
                    render={({ field: { value }, fieldState: { error } }) => (
                      <StyledAutocomplete
                        options={goals}
                        value={goals.find((i) => i.value === value) || null}
                        getOptionLabel={(option) => option.label}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Goal *"
                            variant="outlined"
                            error={error !== undefined}
                            helperText={error?.message}
                          />
                        )}
                        onChange={onGoalChange}
                        groupBy={(e) => e.group}
                        className={classes[">goal"]}
                      />
                    )}
                  />
                </div>
              </div>

              <div className="grid grid-cols-12 gap-6 flex-1 overflow-auto">
                <div className="col-span-12 md:col-span-5 flex flex-col">
                  <InputLabel
                    className={classes[">jsonLabel"]}
                    style={marignBottom05Rem}
                  >
                    Data payload
                  </InputLabel>
                  <CommonJsonWrap className={classes[">json"]}>
                    <JsonEditor
                      name="payload"
                      onChange={() => {
                        setPayloadUsedForGenerate("");
                      }}
                    />
                    {errors.payload && (
                      <FormHelperText
                        error
                        style={{ margin: "0.25rem 1rem 0" }}
                      >
                        {(errors.payload as any)?.message}
                      </FormHelperText>
                    )}
                  </CommonJsonWrap>
                </div>

                <div className="col-span-12 md:col-span-2 flex flex-col items-center justify-center gap-4">
                  {onGenerateHandler && (
                    <>
                      <GenerateButton
                        label="Generate output"
                        disabled={generateDisabled || generating}
                        onClick={onGenerateHandler}
                      >
                        Generate output
                      </GenerateButton>
                      <LoadingSpinner
                        size="sm"
                        style={circularProgressStyle}
                      />
                    </>
                  )}
                </div>

                <div className="col-span-12 md:col-span-5 flex flex-col">
                  <InputLabel
                    style={marignBottom05Rem}
                    className={classes[">jsonLabel"]}
                  >
                    Outcome
                  </InputLabel>
                  <CommonJsonWrap className={classes[">json"]}>
                    <JsonEditor
                      name="outcome"
                      disabled
                    />
                    {errors.outcome && (
                      <FormHelperText
                        error
                        style={{ margin: "0.25rem 1rem 0" }}
                      >
                        {(errors.outcome as any)?.message}
                      </FormHelperText>
                    )}
                  </CommonJsonWrap>
                </div>
              </div>

              <div
                className={`flex items-center justify-end ${classes[">btns"]}`}
                style={{ gap: "1rem" }}
              >
                {isSubmitting && (
                  <LoadingSpinner
                    size="sm"
                    style={{ width: 30, height: 30 }}
                  />
                )}
                <Button
                  type="submit"
                  disabled={isSubmitting || saveIsDisabled || generating}
                  label="Submit"
                >
                  Submit
                </Button>
              </div>
            </div>
          </div>
        );
      }}
    </FieldWatch>
  );
};

export const _: React.FC<IProps> = React.memo((p) => {
  const { scenario, className, onSubmit, onGenerate } = p;

  const [generating, setGenerating] = React.useState(false);
  const [payloadUsedForGenerate, setPayloadUsedForGenerate] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const maybeCurrentRelease = useCurrentRelease();
  const goals = React.useMemo(() => {
    if (!maybeCurrentRelease) return [];

    const g = DecisionGraph.from(maybeCurrentRelease.rule_graph);
    const { goals, derived } = groupGraphNodes(g);

    const allGoals = goals
      .map((n) => ({
        value: n.id,
        label: n.description ?? n.id,
        group: groups.goal,
      }))
      .concat(
        derived.map((n) => ({
          value: n.id,
          label: n.description ?? n.id,
          group: groups.intermediate,
        })),
      );

    return allGoals.sort((a, b) => (a.group === groups.goal && b.group === groups.intermediate ? -1 : 1));
  }, [maybeCurrentRelease]);

  return (
    <Form<ScenarioFormValues>
      defaultValues={scenario}
      schema={testScenarioSchema as any}
      onSubmit={(arg) => {
        setIsSubmitting(true);
        onSubmit({
          ...arg,
          outcome: JSON.parse(arg.outcome),
          payload: JSON.parse(arg.payload),
        });
      }}
    >
      {(form) => (
        <ScenarioFormBody
          form={form}
          className={className}
          goals={goals}
          generating={generating}
          isSubmitting={isSubmitting}
          payloadUsedForGenerate={payloadUsedForGenerate}
          setPayloadUsedForGenerate={setPayloadUsedForGenerate}
          setGenerating={setGenerating}
          onGenerate={onGenerate}
        />
      )}
    </Form>
  );
});
_.displayName = "Components/ManageTestScenario2";
